import { useController, UseControllerProps } from 'react-hook-form'
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormControl,
} from '../../ui/form'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { ChevronDown, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useState, useEffect } from 'react'

export interface ISelectFormData {
  id: string | number | null
  title: string
  subtitle?: string
}

interface Props {
  name: string
  label?: string
  data: ISelectFormData[]
  notFoundMessage?: string
  onChange?: (_: any) => void
}

export function SelectForm(props: Props & UseControllerProps) {
  const { control, name, label, data, notFoundMessage, onChange } = props
  const { fieldState, field } = useController(props)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [selected, setSelected] = useState<ISelectFormData | undefined>(
    data.find((itm) => itm.id === field.value)
  )
  const [searchQuery, setSearchQuery] = useState('')

  // Update selected when field value changes
  useEffect(() => {
    const newSelected = data.find((itm) => itm.id === field.value)
    setSelected(newSelected)
  }, [field.value, data])

  const filteredData = data.filter(
    (item) =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.subtitle?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleSelect = (selectedItem: ISelectFormData) => {
    setIsPopoverOpen(false)
    setSearchQuery('')
    setSelected(selectedItem)
    onChange ? onChange(selectedItem) : field.onChange(selectedItem.id)
  }
  return (
    <div className={'w-full'}>
      <FormField
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem>
            {label && <FormLabel>{label}</FormLabel>}
            <FormControl>
              <Popover
                open={isPopoverOpen}
                onOpenChange={setIsPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={isPopoverOpen}
                    className="h-fit w-full justify-between font-normal"
                  >
                    {selected ? (
                      <div className="flex flex-col items-start">
                        <span>{selected.title}</span>
                        {selected.subtitle && (
                          <span className="text-xs text-muted-foreground">
                            {selected.subtitle}
                          </span>
                        )}
                      </div>
                    ) : (
                      'Seleccionar...'
                    )}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[--radix-popover-trigger-width] p-0"
                >
                  <div className="px-3 pt-3">
                    <div className="relative mb-2">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar..."
                        className="pl-9"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>
                  <div 
                    className="max-h-[200px] overflow-y-auto"
                    onWheel={e => {
                      const target = e.currentTarget;
                      target.scrollTop += e.deltaY;
                    }}
                  >
                    {filteredData.length > 0 ? (
                      filteredData.map((item) => (
                        <div
                          key={item.id}
                          className="cursor-pointer p-2 hover:bg-muted"
                          onClick={() => handleSelect(item)}
                        >
                          <p className="font-medium">{item.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.subtitle}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        {notFoundMessage ?? 'No se encontraron clientes.'}
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </FormControl>
            <FormMessage>{fieldState.error?.message}</FormMessage>
          </FormItem>
        )}
      />
    </div>
  )
}
